import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ButtonComp, TextInputComp, PhoneInputComp, CardComp } from "..";
import InvestorStatusBadge from "./InvestorStatusBadge";
import { investorButtonProps } from "./investorButtonStyles";
import { useInvestorSession } from "../../hooks/useInvestorSession";
import {
  useSubmitInvestorKycMutation,
  useResubmitInvestorKycMutation,
} from "../../store/investor/investorKycApi";
import { getInvestorQueryParams, withInvestorQuery, pickInvestorReturnQuery } from "../../utils/investorRouteHelpers";
import UnifiedTimeline from "../shared/Timeline/UnifiedTimeline";

const schema = Yup.object({
  legal_name: Yup.string().required("Full legal name is required"),
  nin: Yup.string().required("NIN is required").matches(/^\d{11}$/, "NIN must be 11 digits"),
  date_of_birth: Yup.string().required("Date of birth is required"),
  phone: Yup.string().required("Phone is required"),
});

const BLOCKING_STATUSES = new Set(["pending", "under_review", "approved"]);
const RESUBMIT_STATUSES = new Set(["rejected", "resubmission_required"]);

export default function InvestorKycPanel() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = getInvestorQueryParams(searchParams);
  const { profile, kycStatus, kycRecord, refresh } = useInvestorSession();
  const [submitKyc, { isLoading: isSubmittingReal }] = useSubmitInvestorKycMutation();
  const [resubmitKyc, { isLoading: isResubmittingReal }] = useResubmitInvestorKycMutation();

  const continueAfterKyc = () => {
    if (query.redirect) {
      navigate(withInvestorQuery(query.redirect, pickInvestorReturnQuery(query)));
      return;
    }
    navigate("/invest/dashboard");
  };

  const handleRealSubmit = async (values) => {
    try {
      const mutation = RESUBMIT_STATUSES.has(kycStatus) ? resubmitKyc : submitKyc;
      const result = await mutation(values).unwrap();
      toast.success(
        result?.status === "approved"
          ? "Identity verified successfully."
          : "Verification submitted — pending review."
      );
      refresh();
      if (result?.status === "approved") {
        setTimeout(continueAfterKyc, 600);
      }
    } catch (error) {
      toast.error(error?.data?.message || error?.message || "Verification submission failed.");
    }
  };

  const canShowForm = kycStatus === "none" || RESUBMIT_STATUSES.has(kycStatus);
  const showBlockingState = BLOCKING_STATUSES.has(kycStatus);

  return (
    <>
      {showBlockingState ? (
        <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-4">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <span className="fw-bold">Current status</span>
            <InvestorStatusBadge status={kycStatus} />
          </div>
          {kycRecord?.legal_name ? (
            <p className="text-muted small mb-1">Name on file: {kycRecord.legal_name}</p>
          ) : null}
          {kycRecord?.nin_masked ? (
            <p className="text-muted small mb-1">NIN: {kycRecord.nin_masked}</p>
          ) : null}
          {kycRecord?.rejection_reason ? (
            <p className="text-danger small mb-1">Reason: {kycRecord.rejection_reason}</p>
          ) : null}
          {kycRecord?.resubmission_reason ? (
            <p className="text-warning small mb-1">Resubmission required: {kycRecord.resubmission_reason}</p>
          ) : null}
          {kycStatus === "approved" && query.redirect ? (
            <div className="mt-3" style={{ maxWidth: 220 }}>
              <ButtonComp btnText="Continue" onClick={continueAfterKyc} {...investorButtonProps.primary} />
            </div>
          ) : null}
          {(kycStatus === "pending" || kycStatus === "under_review") && (
            <p className="text-muted small mb-0 mt-2">
              Your submission is under review. You can browse the portal, but investment and checkout actions
              stay locked until approval.
            </p>
          )}
          {Array.isArray(kycRecord?.timeline) && kycRecord.timeline.length > 0 ? (
            <div className="mt-3 pt-3 border-top">
              <p className="small fw-semibold mb-2" style={{ color: "#383838" }}>
                Timeline
              </p>
              <UnifiedTimeline items={kycRecord.timeline} showActor={false} compact />
            </div>
          ) : null}
        </CardComp>
      ) : null}

      {canShowForm && (
        <Formik
          initialValues={{
            legal_name: profile?.full_name || kycRecord?.legal_name || "",
            nin: "",
            date_of_birth: kycRecord?.date_of_birth || "",
            phone: profile?.phone || kycRecord?.phone || "",
          }}
          validationSchema={schema}
          enableReinitialize
          onSubmit={handleRealSubmit}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <CardComp cardClassName="border-0 shadow-sm rounded-4">
              <Form>
                <TextInputComp labelText="Full legal name" name="legal_name" />
                <TextInputComp labelText="NIN" name="nin" placeholder="11-digit NIN" />
                <TextInputComp labelText="Date of birth" name="date_of_birth" type="date" />
                <PhoneInputComp
                  LabelText="Phone number"
                  value={values.phone}
                  onChange={(p) => setFieldValue("phone", p)}
                />

                <div className="mt-2" style={{ maxWidth: 280 }}>
                  <ButtonComp
                    type="submit"
                    btnText={
                      RESUBMIT_STATUSES.has(kycStatus) ? "Resubmit verification" : "Submit verification"
                    }
                    loading={isSubmitting || isSubmittingReal || isResubmittingReal}
                    {...investorButtonProps.primary}
                  />
                </div>
              </Form>
            </CardComp>
          )}
        </Formik>
      )}
    </>
  );
}
